/**
 * Header Component
 * Top navigation bar with search, notifications, and user menu
 */

import React, { Fragment, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { LogOut, User, Settings, Menu as MenuIcon, Search, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Avatar, NotificationBell, Button } from '../common';
import { NavThemePicker } from '../landing/NavThemePicker';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useOpenWithReturn } from '../../hooks/useReturnNavigation';
import { usePermissions } from '../../hooks';

export interface HeaderProps {
  onMenuClick?: () => void;
}

// Page title mapping
const getPageTitle = (pathname: string): string => {
  if (pathname === '/dashboard') return 'Dashboard';
  if (pathname === '/okrs') return 'My OKRs';
  if (pathname.startsWith('/okrs/new')) return 'Create OKR';
  if (pathname.startsWith('/okrs/alignment')) return 'Alignment Map';
  if (pathname.startsWith('/okrs/')) return 'OKR Details';
  if (pathname === '/check-ins') return 'Check-ins';
  if (pathname.startsWith('/approvals')) return 'Approvals';
  if (pathname === '/users') return 'Team';
  if (pathname === '/reports') return 'Reports';
  if (pathname === '/admin') return 'Admin';
  if (pathname === '/profile') return 'Profile';
  if (pathname === '/organization') return 'Organization';
  if (pathname === '/super-admin') return 'Platform Admin';
  return 'OKR Hub';
};

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const openWithReturn = useOpenWithReturn();
  const location = useLocation();
  const { canCreateOKR } = usePermissions();
  const [searchQuery, setSearchQuery] = useState('');

  const handleLogout = async () => {
    await logout();
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Pass state with timestamp to trigger re-read in OKRList even when already on /okrs
      navigate(`/okrs?search=${encodeURIComponent(searchQuery.trim())}`, {
        state: { searchTimestamp: Date.now() }
      });
      setSearchQuery('');
    }
  };

  const pageTitle = getPageTitle(location.pathname);

  return (
    <header className="sticky top-0 z-40 border-b border-stone-200 bg-cream/90 backdrop-blur-md">
      <div className="flex h-[72px] items-center justify-between px-4 sm:px-6 lg:px-10">
        {/* Left side - Menu button and page title */}
        <div className="flex items-center gap-4">
          {onMenuClick && (
            <button
              onClick={onMenuClick}
              className="rounded-lg p-2 text-stone-600 hover:bg-stone-100 lg:hidden"
            >
              <MenuIcon className="h-6 w-6" />
            </button>
          )}
          <h1 className="text-2xl font-medium text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
            {pageTitle}
          </h1>
        </div>

        {/* Right side - Search, New OKR button, Notifications and User menu */}
        <div className="flex items-center gap-3">
          {/* Search Box - hidden on mobile */}
          <form onSubmit={handleSearchSubmit} className="relative hidden md:block">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search OKRs..."
              className="h-10 w-72 rounded-full border border-stone-200 bg-white pl-11 pr-4 text-sm placeholder:text-stone-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </form>

          {/* New OKR Button */}
          {canCreateOKR() && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => openWithReturn('/okrs/new')}
              className="hidden sm:flex items-center gap-2 px-5 py-2.5"
            >
              <Plus className="h-4 w-4" />
              <span>New OKR</span>
            </Button>
          )}

          {/* Mobile New OKR Button */}
          {canCreateOKR() && (
            <button
              onClick={() => openWithReturn('/okrs/new')}
              className="flex sm:hidden items-center justify-center w-10 h-10 rounded-full bg-stone-900 text-white"
            >
              <Plus className="h-5 w-5" />
            </button>
          )}

          {/* Theme Picker */}
          <NavThemePicker />

          {/* Notification Bell */}
          <NotificationBell enablePolling={30000} />

          {/* User Menu */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-3 rounded-full p-1 hover:bg-stone-100 transition-colors">
              <Avatar
                size="sm"
                src={user?.profilePictureUrl}
                fallbackText={`${user?.firstName} ${user?.lastName}`}
              />
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-soft-lg ring-1 ring-stone-200 focus:outline-none overflow-hidden">
                {/* User info header */}
                <div className="px-4 py-3 border-b border-stone-100">
                  <p className="text-sm font-semibold text-stone-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-stone-500 capitalize">
                    {user?.role?.replace('_', ' ')}
                  </p>
                </div>

                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/profile"
                        className={`${
                          active ? 'bg-sand' : ''
                        } flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700`}
                      >
                        <User className="h-4 w-4 text-stone-400" />
                        My Profile
                      </Link>
                    )}
                  </Menu.Item>
                  {user?.role === 'account_admin' && (
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/organization"
                          className={`${
                            active ? 'bg-sand' : ''
                          } flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700`}
                        >
                          <Settings className="h-4 w-4 text-stone-400" />
                          Organization Settings
                        </Link>
                      )}
                    </Menu.Item>
                  )}
                  {user?.role === 'super_admin' && (
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/super-admin"
                          className={`${
                            active ? 'bg-sand' : ''
                          } flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700`}
                        >
                          <Settings className="h-4 w-4 text-stone-400" />
                          Platform Settings
                        </Link>
                      )}
                    </Menu.Item>
                  )}
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={`${
                          active ? 'bg-sand' : ''
                        } flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-stone-700`}
                      >
                        <LogOut className="h-4 w-4 text-stone-400" />
                        Logout
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  );
};
